<template>
    <div class="my-8">
        <form @submit.prevent="lookupTyreSizes">
            <CollapseHeight>
                <RegForm
                    v-model:registration="vehicleTyreSizeForm.registration"
                    v-model:size="vehicleTyreSizeForm.size"
                    v-model:type="vehicleTyreSizeForm.lookupType"
                    v-model:prices="cheapestPrices"
                    v-model:defaultView="defaultView"
                ></RegForm>
            </CollapseHeight>
        </form>

        <div v-show="!defaultView">
            <CollapseHeight>
                <AppAlert
                    v-if="error"
                    type="error"
                >
                    <template v-if="error.errors.registration">
                        {{ error.errors.registration[0] }}
                    </template>

                    <template v-else>
                        The
                        <span class="font-bold underline">{{ errorFields.join(", ") }}</span>
                        fields are required.
                    </template>
                </AppAlert>
            </CollapseHeight>

            <CollapseHeight>
                <AppAlert v-if="tyreDetails && tyreDetails.length > 0 && !loading" type="warning" key="form-done">
                    Displaying all sizes for your vehicle. Please
                    <span class="font-bold underline">check your tyre size</span>
                    before you select a tyre to purchase.
                </AppAlert>
            </CollapseHeight>

            <CollapseHeight>
                <AppAlert v-if="noPricesAvailable" type="error" key="form-error">
                    Sorry, but we don't have any prices available at the moment for {{ product.name }}.
                </AppAlert>
            </CollapseHeight>

            <CollapseHeight>
                <AppAlert v-if="tyreDetails && tyreDetails.length === 0 && !noPricesAvailable" type="error" key="form-error">
                    We couldn't find any {{ product.name }} sizes suitable for your vehicle.
                </AppAlert>
            </CollapseHeight>

            <CollapseHeight>
                <div
                    v-if="(!tyreDetails || tyreDetails.length === 0) && !loading && !noPricesAvailable"
                    class="bg-grey-100 h-32 flex items-center justify-center rounded-lg"
                >
                    <Transition name="fade" mode="out-in">
                        <p class="text-dark font-bold text-lg">
                            Enter your reg above to display tyre sizes.
                        </p>
                    </Transition>
                </div>
            </CollapseHeight>

            <CollapseHeight>
                <div
                    v-if="loading"
                    class="bg-grey-100 h-32 flex items-center justify-center rounded-lg"
                >
                    <Transition name="fade" mode="out-in">
                        <font-awesome-icon
                            class="animate-spin transition-all text-dark"
                            size="3x"
                            :icon="['fas', 'tire']"
                        ></font-awesome-icon>
                    </Transition>
                </div>
            </CollapseHeight>

            <CollapseHeight>
                <div v-if="tyreDetails && tyreDetails.length > 0 && !loading">
                    <TyreDetail
                        v-for="(tyreDetail, index) of tyreDetails"
                        :tyre-detail="tyreDetail"
                        :expand="expandTyreDetail === tyreDetail.id"
                        @toggle="toggleExpand(tyreDetail)"
                    ></TyreDetail>
                </div>
            </CollapseHeight>
        </div>
    </div>
</template>

<script>
import RegForm from "@/Components/Products/TyreSizeLookup/RegForm";
import AppAlert from "@/Components/AppAlert";
import TyreDetail from "@/Components/Products/TyreSizeLookup/TyreDetail";
import CollapseHeight from "@/Components/Transitions/CollapseHeight";
import {computed} from "vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
    components: {CollapseHeight, TyreDetail, AppAlert, RegForm, FontAwesomeIcon},
    props: {
        product: Object,
        registration: String
    },
    data() {
        return {
            loading: false,
            tyreDetails: null,
            cheapestPrices: null,
            noPricesAvailable: false,
            expandTyreDetail: null,
            vehicleTyreSizeForm: {
                lookupType: "registration",
                registration: this.registration,
                size: {
                    width: null,
                    profile: null,
                    rim: null,
                },
            },
            error: null
        }
    },
    mounted() {
        this.lookupTyreSizes()
    },
    watch: {
        tyreDetails() {
            if (this.tyreDetails.length > 0) {
                this.expandTyreDetail = this.tyreDetails[0].id;
            }
        }
    },
    provide() {
        return {
            loading: computed(() => this.loading)
        };
    },
    methods: {
        async lookupTyreSizes() {
            this.loading = true;

            try {
                if (this.vehicleTyreSizeForm.registration || this.sizeFields) {
                    const response = await axios.post(
                        this.route("products.tyre-details-lookup", this.product), this.vehicleTyreSizeForm
                    );

                    this.sendGa(this.vehicleTyreSizeForm.registration);

                    this.tyreDetails = response.data;
                } else {
                    const response = await axios.post(
                        this.route("products.tyre-details-lookup", this.product), this.vehicleTyreSizeForm
                    );

                    if (response.data[0]) {
                        this.cheapestPrices = response.data[0].prices;
                    } else {
                        this.noPricesAvailable = true;
                    }
                }
                
                this.error = null;
            } catch (error) {
                if (error.response) {
                    this.error = error.response.data;
                    this.tyreDetails = null;
                }

                throw error;
            } finally {
                this.loading = false;
            }
        },
        sendGa(reg)
        {
            // Analytics V4 Stuff
            if (typeof gtag !== 'undefined')
            {
                gtag('event', 'regSubmit',
                    {
                        'reg': reg
                    });
            }
            else
            {
                console.log("gtag not enabled!");
            }
        },
        toggleExpand(tyreDetail) {
            if (this.expandTyreDetail === tyreDetail.id) {
                this.expandTyreDetail = null;
            } else {
                this.expandTyreDetail = tyreDetail.id;
            }
        }
    },
    computed: {
        errorFields() {
            if (! this.error) return [];

            const fields = Object.keys(this.error.errors);

            return fields.map((field) => {
                return field.replace('size.', '');
            });
        },
        sizeFields() {
            if (this.vehicleTyreSizeForm.size.width &&
                this.vehicleTyreSizeForm.size.profile &&
                this.vehicleTyreSizeForm.size.rim
            ) {
                return true;
            }
            
            return false;
        },
        defaultView() {
            if (
                (this.vehicleTyreSizeForm.registration && this.vehicleTyreSizeForm.lookupType === 'registration') || 
                (this.sizeFields && this.vehicleTyreSizeForm.lookupType === 'size') || 
                this.error ||
                this.noPricesAvailable || 
                (this.tyreDetails && this.tyreDetails.length > 0)
            ) {
                return false;
            }

            return true;
        }
    },

}
</script>

<style scoped>

</style>
